import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  InfiniteScroll,
  LoadingSpinner,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiFunnel } from 'react-icons/pi';
import { XIcon } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { SimpleFilter, useFilterContext } from '../FilterContext';
import { Filter, useGetFilters } from '@ais-ui/websockets';
import { CheckIcon } from '@radix-ui/react-icons';
import { cn } from '@ais-ui/utils';
import {
  useSidebarItemAction,
  filtersButtonId,
} from '../../../components/sidebar/useSidebarItemAction';

const ExistingFilter = ({ onOpenSidebar }: { onOpenSidebar: () => void }) => {
  const { messages } = useLocalization();
  const filterContext = useFilterContext();
  const { data: filters, isLoading } = useGetFilters();
  const [search, setSearch] = useState('');
  const handleSelect = (filter: Filter) => {
    // Check if the uuid already exists in customFilters
    const uuidExists = filterContext.customFilters.some(
      (existingFilter) => existingFilter.uuid === filter.uuid,
    );

    if (!uuidExists) {
      // If the uuid doesn't exist, add the new filter
      filterContext.setCustomFilters([...filterContext.customFilters, filter]);
    } else {
      filterContext.setCustomFilters(
        filterContext.customFilters.filter(
          (existingFilter) => existingFilter.uuid !== filter.uuid,
        ),
      );
    }
  };

  const createSafeValue = (filter: Filter) => {
    // Remove or replace problematic characters, otherwise we'll crash the application for example for:
    // evi_classification_ = "DET NORSKE"
    const safeName = filter.name.replace(/[^a-zA-Z0-9\-_]/g, '_');
    return `${safeName}|${filter.uuid}`;
  };

  const hasFilters = !isLoading && filters && filters.length > 0;
  const isEmpty = !isLoading && (!filters || filters.length === 0);

  if (isEmpty) {
    return (
      <div className="flex flex-col items-center justify-center px-3 py-6">
        <img
          className="max-h-32 w-[50%] mb-3"
          src="empty_list_bro.avif"
          alt="No filters"
        />
        <p className="text-sm text-muted-foreground text-center mb-1">
          No filters created yet.
        </p>
        <button
          type="button"
          onClick={onOpenSidebar}
          className="text-sm text-blue-500 hover:underline"
        >
          Create filters
        </button>
      </div>
    );
  }

  return (
    <Command>
      <CommandInput
        placeholder="Search for filter..."
        variant="bordered"
        onValueChange={(val) => setSearch(val)}
      />
      {!isLoading && (
        <CommandEmpty>{messages.mapMenuFilterNoFiltersFound}</CommandEmpty>
      )}
      <CommandList>
        <CommandGroup heading="Apply filters">
          {isLoading && <LoadingSpinner size="small" />}
          {hasFilters && (
            <div className="h-[200px]">
              <InfiniteScroll
                items={filters}
                limit={10}
                search={search}
                observeResize={true}
                filterFn={(item, searchTerm) =>
                  item.name.toLowerCase().includes(searchTerm.toLowerCase())
                }
                sortFn={(a, b) => a.name.localeCompare(b.name)}
                renderItem={(filter: Filter) => {
                  return (
                    <CommandItem
                      key={filter.uuid}
                      value={createSafeValue(filter)}
                      onSelect={() => handleSelect(filter)}
                    >
                      <div className="flex justify-between w-full">
                        <div>{filter.name}</div>
                        {filterContext.customFilters.some(
                          (customFilter) => customFilter.uuid === filter.uuid,
                        ) && <CheckIcon className="size-4" />}
                      </div>
                    </CommandItem>
                  );
                }}
                className="h-full pr-2 pt-2"
              ></InfiniteScroll>
            </div>
          )}
        </CommandGroup>
      </CommandList>
    </Command>
  );
};

interface FilterBadgeProps {
  filter: Filter | SimpleFilter;
  className?: string;
}

const isCustomFilter = (filter: Filter | SimpleFilter): filter is Filter =>
  (filter as Filter).uuid !== undefined;

export const FilterBadge = ({ filter, className }: FilterBadgeProps) => {
  const filterContext = useFilterContext();

  const onBadgeClick = () => {
    if (isCustomFilter(filter)) {
      filterContext.setCustomFilters(
        filterContext.customFilters.filter((item) => item.uuid !== filter.uuid),
      );
    } else {
      filterContext.setSimpleFilters(
        filterContext.simpleFilters.filter((item) => item.id !== filter.id),
      );
    }
  };

  return (
    <div
      key={filter.name}
      className={cn('relative group px-1 py-0.5', className)}
    >
      <Badge className="space-x-2 hover:cursor-pointer" onClick={onBadgeClick}>
        <p>{filter.name}</p>
        <XIcon className="size-4" />
      </Badge>
    </div>
  );
};

const ActiveFilters = () => {
  const { messages } = useLocalization();
  const filterContext = useFilterContext();
  const allFilters = [
    ...filterContext.simpleFilters,
    ...filterContext.customFilters,
  ];

  return (
    <div className="px-3 py-2">
      <p className="text-xs font-medium text-muted-foreground mb-1">
        Active filters
      </p>
      {allFilters.length === 0 && (
        <div className="py-2 text-center text-sm">
          {messages.mapMenuFilterNoFiltersApplied}
        </div>
      )}
      {allFilters.length >= 1 && (
        <div className="flex flex-wrap">
          {filterContext.customFilters.map((filter) => (
            <FilterBadge key={filter.uuid} filter={filter}></FilterBadge>
          ))}
          {filterContext.simpleFilters.map((filter) => (
            <div key={filter.name} className="relative group px-1 py-0.5">
              <Badge className="space-x-2">
                <p>{filter.name}</p>
                <XIcon className="size-4" />
              </Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface FilterShipsProps {
  className?: string;
}

export const FilterShips = React.memo(({ className }: FilterShipsProps) => {
  const { messages } = useLocalization();
  const [open, setOpen] = React.useState(false);
  const filterContext = useFilterContext();
  const onSidebarItemClick = useSidebarItemAction();

  const handleOpenFilterSidebar = () => {
    setOpen(false);
    onSidebarItemClick(filtersButtonId);
  };

  const allFilters = useMemo(
    () => [...filterContext.simpleFilters, ...filterContext.customFilters],
    [filterContext.simpleFilters, filterContext.customFilters],
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <TooltipTrigger asChild>
              <Button
                id="filter-tool-button"
                size="icon"
                variant="outline"
                onClick={() => setOpen((prev) => !prev)}
                className={cn(
                  'flex items-center justify-center size-8 p-1 group/btn rounded-lg',
                  className,
                )}
              >
                <div className="relative">
                  <PiFunnel className="size-5 group-hover/btn:text-primary" />
                  {allFilters.length > 0 && (
                    <span className="absolute -top-2 -right-2.5 inline-flex items-center justify-center px-1 py-0.5 text-xs font-bold leading-none text-red-100 bg-primary rounded-full">
                      {allFilters.length}
                    </span>
                  )}
                </div>
              </Button>
            </TooltipTrigger>
          </PopoverTrigger>
          <TooltipContent side="right" sideOffset={4}>
            {messages.mapFilterByButtonTooltip}
          </TooltipContent>
          <PopoverContent side="right" align="end" className="p-0 shadow-sm">
            <div className="px-3 pt-3 pb-1">
              <h3 className="text-sm font-semibold">Filters</h3>
            </div>
            <ActiveFilters />
            <ExistingFilter onOpenSidebar={handleOpenFilterSidebar} />
          </PopoverContent>
        </Popover>
      </Tooltip>
    </TooltipProvider>
  );
});
